import { useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn } from "lucide-react";

const loadImage = async (src) => {
  // Respect EXIF orientation so phone portraits don't crop sideways.
  if (typeof createImageBitmap === "function") {
    try {
      const blob = await (await fetch(src)).blob();
      return await createImageBitmap(blob, { imageOrientation: "from-image" });
    } catch { /* fall through to <img> */ }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/** Render the chosen crop area to a JPEG file, capped at 1024px on the long edge. */
export async function cropToFile(src, area, name = "headshot.jpg", max = 1024) {
  const img = await loadImage(src);
  const scale = Math.min(1, max / Math.max(area.width, area.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(area.width * scale));
  canvas.height = Math.max(1, Math.round(area.height * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
  return new File([blob], name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
}

export function ImageCropDialog({
  open, src, aspect = 1, round = false, filename = "headshot.jpg",
  onCancel, onCropped, testId = "crop",
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!area) return;
    setBusy(true);
    try {
      onCropped(await cropToFile(src, area, filename));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle>Frame the photo</DialogTitle>
          <DialogDescription>
            Drag to position, pinch or zoom to size. Everything outside the frame is trimmed.
          </DialogDescription>
        </DialogHeader>
        <div className="relative h-[320px] w-full overflow-hidden rounded-xl bg-black/80">
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect}
                     cropShape={round ? "round" : "rect"} showGrid={!round}
                     restrictPosition minZoom={1} maxZoom={4}
                     onCropChange={setCrop} onZoomChange={setZoom}
                     onCropComplete={(_, pixels) => setArea(pixels)} />
          )}
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomIn className="h-4 w-4 text-muted-foreground" />
          <Slider value={[zoom]} min={1} max={4} step={0.05} data-testid={`${testId}-zoom`}
                  onValueChange={([v]) => setZoom(v)} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={apply} disabled={busy || !area} data-testid={`${testId}-apply`}>
            {busy ? "Trimming…" : "Use this crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
